import $localStorage   from '@/services/storage/localStorage';
import $sessionStorage from '@/services/storage/sessionStorage';
import $axios          from '@/services/axios';

const OAuth2 = {
    initialized: false,
    providers: {},
    config: {
        enableLoginWithCredentials: true,
        tokenKey: 'oauth2-token',
        refreshKey: 'oauth2-refresh',
        useSessionStorage: false,
        tokenEndpoint: '/token',
        client_id: null,
        client_secret: null,
    },
    configProviders: {},
    accessToken: null,
    refreshToken: null,
    refreshingToken: {
        active: false,
        subscribers: [],
    },
    onLoggedIn: null,
    onLoggedOut: null,
    me: null,
};

// Config
OAuth2.init = (config) => {
    OAuth2.initialized = true;
    OAuth2.setConfig(config);
};
OAuth2.setConfig = (config) => OAuth2.config = Object.assign({}, OAuth2.config, config);

// Tokens
OAuth2.getStorage = () => OAuth2.config.useSessionStorage ? $sessionStorage : $localStorage;
OAuth2.tokens = {
    access: {
        get: () => {
            if (null === OAuth2.accessToken) {
                OAuth2.accessToken = OAuth2.getStorage().get(OAuth2.config.tokenKey);
            }

            return OAuth2.accessToken;
        },
        set: (token) => {
            OAuth2.getStorage().put(OAuth2.config.tokenKey, token);
            OAuth2.accessToken = token;
        },
        remove: () => {
            OAuth2.getStorage().delete(OAuth2.config.tokenKey);
            OAuth2.accessToken = null;
        },
    },
    refresh: {
        get: () => {
            if (null === OAuth2.refreshToken) {
                OAuth2.refreshToken = OAuth2.getStorage().get(OAuth2.config.refreshKey);
            }

            return OAuth2.refreshToken;
        },
        set: (token) => {
            OAuth2.getStorage().put(OAuth2.config.refreshKey, token);
            OAuth2.refreshToken = token;
        },
        remove: () => {
            OAuth2.getStorage().delete(OAuth2.config.refreshKey);
            OAuth2.refreshToken = null;
        },
    },
};
OAuth2.tokens.reset = () => {
    OAuth2.tokens.access.remove();
    OAuth2.tokens.refresh.remove();
};

// Http Requests
OAuth2.getAxiosInstance = () => $axios.getOAuth2TokenInstance();
OAuth2.requestToken = (options) => {
    return OAuth2
        .getAxiosInstance()
        .post(OAuth2.config.tokenEndpoint, Object.assign({}, {
            client_id:     OAuth2.config.client_id,
            client_secret: OAuth2.config.client_secret,
        }, options))
    ;
};

// Credentials
OAuth2.loginRequest = (options) => {
    return OAuth2
        .requestToken(Object.assign({}, options, {onLoggedIn: null}))
        .then(response => {
            OAuth2.tokens.access.set(response.data.access_token);
            OAuth2.tokens.refresh.set(response.data.refresh_token);

            if (options.onLoggedIn && typeof options.onLoggedIn === 'function') {
                options.onLoggedIn();
            } else if (OAuth2.onLoggedIn && typeof OAuth2.onLoggedIn === 'function') {
                OAuth2.onLoggedIn();
            }

            return OAuth2.getMe();
        })
        .catch(() => {
            return OAuth2.logout()
                .then(() => {
                    throw Error('Failed login');
                })
            ;
        })
    ;
};
OAuth2.loginWithCredentials = (username, password) => {
    return new Promise((resolve, reject) => {
        if (!OAuth2.config.enableLoginWithCredentials) {
            /* eslint-disable */
            console.warn('[Auth] Login with credentials is disabled');
            throw Error('Login with credentials is disabled');
        }

        OAuth2
            .loginRequest({
                grant_type: 'password',
                username,
                password,
            })
            .then(r => resolve(r))
            .catch(e => reject(e))
        ;
    });
};
OAuth2.logout = () => {
    return new Promise(resolve => {
        OAuth2.tokens.reset();
        OAuth2.me = null;

        if (OAuth2.onLoggedOut && typeof OAuth2.onLoggedOut === 'function') {
            OAuth2.onLoggedOut();
        }

        resolve();
    });
};
OAuth2.setLoginCallback = (callback) => OAuth2.onLoggedIn = callback;
OAuth2.setLogoutCallback = (callback) => OAuth2.onLoggedOut = callback;
OAuth2.getMe = () => {
    return new Promise((resolve, reject) => {
        if (OAuth2.me) {
            resolve(OAuth2.me);

            return;
        }

        if (!OAuth2.tokens.access.get()) {
            resolve(null);

            return;
        }

        $axios
            .get('/me')
            .then(res => res.data)
            .then(me => {
                OAuth2.me = me;

                resolve(OAuth2.me);
            })
            .catch(() => {
                OAuth2.me = null;

                OAuth2
                    .logout()
                    .then(() => {
                        resolve(OAuth2.me);
                    })
                ;
            })
        ;
    });
};

// Refresh Token
OAuth2.refreshingToken.subscribe = (callback, callbackError) => OAuth2.refreshingToken.subscribers.push({
    resolve: callback,
    error:   callbackError,
});
OAuth2.refreshingToken.success = (token, refresh) => {
    return new Promise(resolve => {
        OAuth2.tokens.access.set(token);
        OAuth2.tokens.refresh.set(refresh);

        OAuth2.refreshingToken.subscribers.map(subscriber => subscriber.resolve(token));
        OAuth2.refreshingToken.subscribers = [];

        resolve();
    });
};
OAuth2.refreshingToken.denied = () => {
    return OAuth2
        .logout()
        .then(() => {
            OAuth2.refreshingToken.subscribers.map(subscriber => subscriber.error());
            OAuth2.refreshingToken.subscribers = [];
        })
        .catch(() => {})
    ;
};
OAuth2.refresh = () => {
    if (OAuth2.refreshingToken.active) {
        return;
    }

    OAuth2.refreshingToken.active = true;

    OAuth2
        .requestToken({
            grant_type: 'refresh_token',
            refresh_token: OAuth2.tokens.refresh.get(),
        })
        .then(response => [response.data.access_token, response.data.refresh_token])
        .then(([AccessToken, RefreshToken]) => OAuth2.refreshingToken.success(AccessToken, RefreshToken))
        .catch(() => OAuth2.refreshingToken.denied())
        .finally(() => OAuth2.refreshingToken.active = false)
    ;
};

// Providers
OAuth2.addProvider = (provider, config) => {
    const name = provider.name || 'default';

    if (OAuth2.hasProvider(name)) {
        return;
    }

    provider.config = Object.assign({}, OAuth2.config, config || {});
    OAuth2.providers[name] = provider;
};
OAuth2.hasProvider = (providerName) => !!OAuth2.providers[providerName];
OAuth2.getProvider = (providerName) => {
    return new Promise((resolve, reject) => {
        if (!OAuth2.hasProvider(providerName)) {
            reject(new Error('Can\'t find provider "' + providerName + '"'));

            return;
        }

        const provider = OAuth2.providers[providerName];

        if (!provider.init || typeof provider.init !== 'function') {
            reject(new Error('Provider "' + providerName + '" is not valid.'));

            return;
        }

        if (!provider.initialized) {
            provider
                .init(provider.config)
                .then(() => {
                    provider.initialized = true;

                    resolve(provider);
                })
                .catch(() => reject(new Error('Can\'t init provider "' + providerName + '"')))
            ;

            return;
        }

        resolve(provider);
    });
};
OAuth2.loginWithProvider = (providerName) => {
    return new Promise((resolve, reject) => {
        OAuth2
            .getProvider(providerName)
            .then(provider => {
                if (!provider.getAuthorizationCode || typeof provider.getAuthorizationCode !== 'function') {
                    throw Error('Failed login');
                }

                return provider.getAuthorizationCode();
            })
            .then(ac => {
                OAuth2
                    .loginRequest({
                        grant_type: providerName,
                        code: ac,
                    })
                    .then(r => resolve(r))
                    .catch(e => reject(e))
                ;
            })
            .catch(() => reject(Error('Failed login')))
        ;
    });
};

export default OAuth2;
